import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import { getApiUrl } from '../../../utils/apiConfig';

const ClienteDropdown = ({ selectedVisita, setSelectedVisita }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const timeoutRef = useRef();
  const abortControllerRef = useRef();
  const cacheRef = useRef(new Map());

  const norm = (s) => String(s || '').trim().toLowerCase();

  // Buscar con caché + cancelación
  const searchClientes = useCallback(async (searchTerm) => {
    const trimmedTerm = norm(searchTerm);

    if (trimmedTerm.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    if (cacheRef.current.has(trimmedTerm)) {
      const cachedResult = cacheRef.current.get(trimmedTerm);
      setOptions(cachedResult);
      setLoading(false);
      setError(cachedResult.length === 0 ? 'No se encontró ningún cliente' : null);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    try {
      const res = await axios.get(
        getApiUrl(`/api/VisitasCliente?q=${encodeURIComponent(searchTerm)}`),
        {
          withCredentials: true,
          signal: abortControllerRef.current.signal,
          timeout: 8000,
        }
      );

      const data = Array.isArray(res.data) ? res.data : [];
      cacheRef.current.set(trimmedTerm, data);
      if (cacheRef.current.size > 50) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }

      setOptions(data);
      setLoading(false);
      setError(data.length === 0 ? 'No se encontró ningún cliente' : null);
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('La búsqueda está tardando mucho, intenta de nuevo');
      } else if (err.response) {
        if (err.response.status >= 500) setError('Error del servidor, intenta más tarde');
        else if (err.response.status === 404) setError('Servicio no disponible');
        else setError('Error al buscar clientes');
      } else if (err.request) {
        setError('Revisa tu conexión a internet');
      } else {
        setError('Error temporal, intenta de nuevo');
      }
      setLoading(false);
    }
  }, []);

  // Input con debounce
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsOpen(true);
    setError(null);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (value.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timeoutRef.current = setTimeout(() => searchClientes(value), 400);
  }, [searchClientes]);

  const handleSelect = useCallback((cliente) => {
    const cleanCliente = cliente.Cliente?.replace(/\s+$/, '');
    setSelectedVisita({
      ...selectedVisita,
      Cliente: cleanCliente,
      IdCliente: cliente.IdCliente,
    });
    setInputValue(`${cleanCliente} | ${cliente.IdCliente}`);
    setIsOpen(false);
    setLoading(false);
  }, [selectedVisita, setSelectedVisita]);

  // Crea cliente y selecciona
  const createAndSelect = useCallback(async (nombre) => {
    const clean = nombre.trim();
    if (clean.length < 2 || creating) return;
    try {
      setCreating(true);
      const postRes = await fetch(getApiUrl('/api/ClienteFueraDeAS400'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Cliente: clean }),
        credentials: 'include',
      });
      if (!postRes.ok) throw new Error('Error al crear el cliente');
      const resp = await postRes.json();
      setSelectedVisita(v => ({ ...v, Cliente: clean, IdCliente: resp.IdCliente }));
      setInputValue(`${clean} | ${resp.IdCliente}`);
      setIsOpen(false);
      // invalidar cache justo del término
      cacheRef.current.delete(norm(clean));
    } catch (e) {
      setError('Error al crear el cliente');
    } finally {
      setCreating(false);
    }
  }, [creating, setSelectedVisita]);

  const trimmed = inputValue.trim();
  const hasPipe = inputValue.includes('|');
  const hasExact = trimmed.length >= 2 && options.some(
    c => norm(c.Cliente) === norm(trimmed) || String(c.IdCliente) === trimmed
  );

  // Enter para crear si no hay match exacto
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    const q = inputValue.trim();
    if (q.length < 2) return;

    if (hasPipe) return; // No crear si ya tiene pipe (formato correcto)

    const exact = options.some(
      c => norm(c.Cliente) === norm(q) || String(c.IdCliente) === q
    );
    if (!exact) {
      e.preventDefault();
      createAndSelect(q);
    }
  }, [inputValue, options, createAndSelect, hasPipe]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Cerrar al click fuera
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Sincroniza input cuando seleccionan desde fuera
  useEffect(() => {
    if (selectedVisita?.Cliente && selectedVisita?.IdCliente) {
      const cleanCliente = selectedVisita.Cliente?.replace(/\s+$/, '');
      setInputValue(`${cleanCliente} | ${selectedVisita.IdCliente}`);
    }
  }, [selectedVisita?.Cliente, selectedVisita?.IdCliente]);

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="block mb-1 text-xs font-medium text-gray-700 truncate">Cliente*</label>
      <input
        type="text"
        className="DropDownName w-full p-2 shadow-sm border rounded bg-white truncate"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Buscar cliente por Nombre o ID…"
        autoComplete="off"
      />

      {isOpen && inputValue && (
        <ul className="absolute z-10 mt-1 w-full bg-white border rounded shadow-lg max-h-60 overflow-auto">
          {loading && (
            <li className="px-4 py-2 text-sm text-gray-400 flex items-center">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 mr-2"></div>
              Buscando...
            </li>
          )}

          {error && !loading && (
            <li className="px-4 py-2 text-sm text-red-500 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </li>
          )}

          {/* Crear nuevo si no hay match exacto y hay texto suficiente */}
          {!loading && !hasExact && trimmed.length >= 2 && !hasPipe && (
            <li
              onClick={() => createAndSelect(trimmed)}
              className="cursor-pointer px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {creating ? 'Creando…' : <>Crear nuevo cliente “{trimmed}”</>}
            </li>
          )}

          {/* Resultados */}
          {!error && options.length > 0 && options.map((cliente) => {
            const cleanName = cliente.Cliente?.replace(/\s+$/, '');
            const isSelected =
              selectedVisita?.IdCliente === cliente.IdCliente &&
              selectedVisita?.Cliente === cliente.Cliente;
            return (
              <li
                key={cliente.IdCliente}
                onClick={() => handleSelect(cliente)}
                className={classNames('cursor-pointer px-4 py-2 text-sm', { 'bg-gray-100': isSelected })}
                style={{ backgroundColor: isSelected ? '#f3f4f6' : 'white' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eee')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#f3f4f6' : 'white')}
              >
                {cleanName} <span className="text-gray-400">| {cliente.IdCliente}</span>
              </li>
            );
          })}

          {!loading && !error && options.length === 0 && trimmed.length >= 2 && hasExact && (
            <li className="px-4 py-2 text-sm text-gray-400">No hay más resultados</li>
          )}

          {!loading && !error && trimmed.length < 2 && (
            <li className="px-4 py-2 text-sm text-blue-500">Escribe al menos 2 caracteres para buscar</li>
          )}
        </ul>
      )}
    </div>
  );
};


export default ClienteDropdown;
